import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { contarArrastres, type Coincidencia } from "./filtroAsignaciones";
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  useReactFlow,
  useStore,
  useStoreApi,
  type Edge,
  type FitViewOptions,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  etiquetaModelo,
  modelosParaTarget,
  origenConocido,
  type Asignacion,
  type ModeloCurado,
} from "@/lib/api/modelosApi";
import {
  ANCHO_NODO,
  CABECERA_GRUPO,
  altoNodo,
  construirLayout,
  limitesLayout,
  type DatosGrupo,
  type DatosNodo,
  type Rectangulo,
} from "./grafoLayout";
import InsigniasAgente from "./InsigniasAgente";
import { PADDING_ENCUADRE, ZOOM_ENCUADRE, calcularEncuadre } from "./encuadre";

/**
 * Vista de GRAFO del bloque de asignaciones del panel de modelos.
 *
 * Existe porque la estructura que se configura aqui dejo de ser una lista al
 * aparecer los subagentes: un subagente hereda del agente padre si no tiene
 * asignacion propia, y esa relacion es justo lo que una tabla de filas planas
 * NO enseña. En el grafo la arista la dice sola.
 *
 * React Flow y no Mermaid (que ya esta instalado) por una razon concreta: aqui
 * los nodos son componentes React de verdad, asi que el `<select>` de modelo
 * vive DENTRO de la cajita. Mermaid renderiza a SVG y obligaria a sacar la
 * edicion a un panel lateral, que es exactamente la separacion entre "lo que
 * veo" y "lo que toco" que esta vista existe para cerrar.
 *
 * **La topologia es de solo lectura**: no se crean ni se editan conexiones, no
 * se arrastran nodos. Lo unico editable es el modelo. Se dejan el pan y el zoom,
 * que si ayudan cuando el grafo no cabe.
 *
 * La tabla NO se sustituye: convive con esta vista tras un conmutador
 * (`SeccionModelos`). Un canvas de nodos es la peor alternativa posible con
 * lector de pantalla o en una ventana estrecha, y esa salida tiene que existir.
 *
 * **Dos grupos visuales, Agentes y Tareas**, como sub flows de React Flow: un
 * nodo contenedor (`grupo`) y sus hijos con `parentId`. Es pertenencia, no
 * jerarquia: los subagentes siguen colgando de su agente con una arista dentro
 * del grupo de Agentes. La geometria de los grupos la decide `grafoLayout.ts`.
 */


/** Ids de los datos que el nodo necesita y que no viajan en la `Asignacion`. */
export interface ContextoGrafo {
  modelos: ModeloCurado[];
  onAsignar: (a: Asignacion, modelId: string | null) => void;
  /** `contexto` = se pinta solo para no dejar huerfano a un hijo que si casa. */
  papel: Coincidencia;
  /**
   * Cuantos subagentes arrastraria un cambio en ESTE agente.
   *
   * Solo lo traen los agentes con subagentes heredados. 0 (o ausente) = el
   * cambio no alcanza a nadie mas y no hay nada que avisar.
   */
  arrastra: number;
  /** El padre de este subagente esta cambiando de modelo ahora mismo. */
  arrastrado: boolean;
}

type NodoTarget = Node<DatosNodo, "targetModelo">;

/**
 * Cajita de un target. UN solo componente parametrizado por `target_type`, no
 * tres casi iguales: lo unico que cambia entre un agente, un subagente y una
 * operacion es el texto de la primera opcion del desplegable y si tiene
 * conectores de arista.
 */
function NodoTargetModelo({ data, height }: NodeProps<NodoTarget>) {
  const { t } = useTranslation();
  const {
    asignacion: a,
    modelos,
    onAsignar,
    papel,
    arrastra,
    arrastrado,
  } = data as DatosNodo & ContextoGrafo;
  const origen = origenConocido(a.origen);
  const esSubagente = a.target_type === "subagent";
  const esAgente = a.target_type === "agent";

  const disponibles = useMemo(
    () => modelosParaTarget(modelos, a.target_type),
    [modelos, a.target_type],
  );

  const etiquetaOrigen =
    origen === "asignado"
      ? t("admin.modelos.origenAsignado")
      : origen === "heredado"
        ? t("admin.modelos.origenHeredado")
        : t("admin.modelos.origenDefault");

  const colorOrigen =
    origen === "asignado"
      ? "bg-blue-500/15 text-blue-600"
      : origen === "heredado"
        ? "bg-amber-500/15 text-amber-600"
        : "bg-[var(--muted)] text-[var(--muted-foreground)]";

  return (
    <div
      // Tres estados visuales que NO se pisan:
      // - `contexto`: atenuado, porque el filtro no lo encontro y solo esta
      //   aqui para que su hijo no quede huerfano.
      // - `arrastrado`: resaltado en ambar, porque el modelo de su padre acaba
      //   de cambiar y el suyo se ha movido con el sin tocarlo nadie.
      // Se marca ademas con `data-papel` para que un test pueda afirmar el
      // estado sin depender de que clase de Tailwind se use hoy.
      data-papel={papel}
      data-arrastrado={arrastrado ? "si" : undefined}
      // Los bloques van pegados con `gap-1.5`, NO repartidos con
      // `justify-between`: el alto lo declara el layout para ESTE contenido
      // (`altoNodo`), asi que repartir el sobrante solo abriria huecos que
      // parecerian un fallo. Y el alto viene del nodo del layout (React Flow lo
      // pasa en las props), con la misma funcion de respaldo: si alguien
      // montara el nodo sin el, seguiria midiendo lo que el layout calcula.
      // Cada bloque lleva `shrink-0`: si el alto declarado se quedara corto,
      // el contenido debe DESBORDAR (se ve) y no encogerse — sin ello el flex
      // aplasta el id del modelo, que tiene `overflow: hidden` por el
      // `truncate`, hasta 0 px sin que nada avise (medido en Chrome).
      className={`flex h-full w-full flex-col gap-1.5 rounded-lg border bg-[var(--background)] px-3 py-2 shadow-sm ${
        arrastrado
          ? "border-amber-500 ring-2 ring-amber-500/40"
          : "border-[var(--border)]"
      } ${papel === "contexto" ? "opacity-40" : ""}`}
      style={{ width: ANCHO_NODO, height: height ?? altoNodo(a, arrastra) }}
    >
      {/* Conectores. Solo donde hay arista: un agente los ofrece a su derecha y
          un subagente los recibe por la izquierda. Una operacion auxiliar no
          lleva ninguno porque no cuelga de nadie — pintarle un conector vacio
          insinuaria una relacion que no existe. */}
      {esAgente && <Handle type="source" position={Position.Right} isConnectable={false} />}
      {esSubagente && <Handle type="target" position={Position.Left} isConnectable={false} />}

      <div className="flex shrink-0 items-start justify-between gap-2">
        <span className="truncate text-sm font-medium" title={a.target_id}>
          {a.label || a.target_id}
        </span>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${colorOrigen}`}>
          {etiquetaOrigen}
        </span>
      </div>

      <span
        className="shrink-0 truncate font-mono text-[11px] text-[var(--muted-foreground)]"
        title={a.model_id}
      >
        {a.model_id}
      </span>

      {/* Que CLASE de agente es. Va aqui, junto al nombre y encima del
          selector, porque es lo que decide si a este agente le conviene un
          modelo mas barato: un autonomo que procesa correos de noche no pide lo
          mismo que uno con el que se conversa.
          Una operacion auxiliar no pinta ninguna (`caracteristicas` llega
          `null`) y el componente devuelve `null`: la fila desaparece entera. */}
      <InsigniasAgente
        caracteristicas={a.caracteristicas}
        esSubagente={esSubagente}
        className="shrink-0"
      />

      {/* El alcance real del cambio, en la caja donde se hace.
          La precedencia elegida es "gana lo mas especifico": asignar modelo al
          agente arrastra a los subagentes SIN asignacion propia y deja quieto al
          que tiene la suya. Sin decirlo aqui, quien cambia `especificaciones`
          cree que toca un target y toca cuatro. Es un aviso, no un modal: en el
          caso normal se lee de paso y no interrumpe nada.
          Sin aviso NO se reserva la linea: la caja es mas baja y el layout ya
          lo sabe (`altoNodo` suma la linea solo cuando `arrastra > 0`). */}
      {arrastra > 0 && (
        <span className="shrink-0 truncate text-[10px] text-amber-600">
          {t("admin.modelos.arrastraSubagentes", { count: arrastra })}
        </span>
      )}

      <div className="flex shrink-0 items-center gap-1">
        <select
          // `nodrag` y `nowheel` NO son cosmeticos: React Flow captura los
          // eventos de puntero del canvas para el pan y el zoom, asi que sin
          // estas clases el desplegable NO ABRE (el mousedown se lo queda el
          // canvas y arrastra el grafo) y la rueda sobre la lista hace zoom en
          // lugar de recorrer las opciones.
          className="nodrag nowheel min-w-0 flex-1 rounded-lg border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-xs"
          // `value` es el modelo asignado, o "" cuando el target NO tiene fila
          // propia: el modelo efectivo de un heredado o de un default puede no
          // estar ni dado de alta, y ponerlo de `value` haria que el navegador
          // eligiera la primera opcion y el desplegable afirmara una asignacion
          // que no existe.
          value={origen === "asignado" ? a.model_id : ""}
          aria-label={t("admin.modelos.asignarAria", { target: a.label || a.target_id })}
          onChange={(e) => onAsignar(a, e.target.value || null)}
        >
          {/* Para un subagente la opcion vacia NO es "usar el modelo por
              defecto": es "usar el del agente", que es otra cosa y puede ser
              otro modelo. Llamarlas igual escondería justo la herencia que este
              grafo existe para enseñar. */}
          <option value="">
            {esSubagente ? t("admin.modelos.usarDelAgente") : t("admin.modelos.usarDefault")}
          </option>
          {disponibles.map((m) => (
            // Nombre Y precio por millon: elegir modelo sin ver el precio es la
            // decision cara de esta pantalla, y el dato ya viene en la lista.
            <option key={m.model_id} value={m.model_id}>
              {etiquetaModelo(m)}
            </option>
          ))}
        </select>
        {origen === "asignado" && (
          <button
            type="button"
            className="nodrag shrink-0 rounded p-1 hover:bg-[var(--muted)]"
            onClick={() => onAsignar(a, null)}
            title={
              esSubagente
                ? t("admin.modelos.volverAlAgente")
                : t("admin.modelos.volverDefault")
            }
            aria-label={
              esSubagente
                ? t("admin.modelos.volverAlAgenteAria", { target: a.label || a.target_id })
                : t("admin.modelos.volverDefaultAria", { target: a.label || a.target_id })
            }
          >
            <RotateCcw size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

type NodoContenedor = Node<DatosGrupo, "grupo">;

/**
 * Marco de un grupo (Agentes o Tareas). Solo titulo y fondo: no es editable ni
 * seleccionable, y no lleva selector. El tamaño lo pone el layout por su
 * contenido y React Flow lo aplica al envoltorio; aqui basta con llenarlo.
 *
 * Fondo SUAVE y borde discreto a proposito: la funcion del marco es que el ojo
 * separe los dos conjuntos, no competir con las cajas — que llevan el color en
 * los chips de origen y en las insignias. Dos grupos con dos colores fuertes
 * serian un arcoiris encima de otro.
 *
 * Por eso el marco va al 40 % de `--muted` y su borde al 60 %, y no al token
 * entero: a plena opacidad se lee como un PANEL y pesa mas que su contenido.
 * En oscuro el salto es el visible (`#333333` sobre un fondo `#242424`), pero
 * la opacidad se aplica en los dos temas porque el problema es el mismo —
 * nada de una regla `dark:`, que en este repo NO sigue al tema (ver la nota de
 * Tailwind v4 en el front: el tema va por variables CSS, no por la clase).
 */
function NodoGrupo({ data }: NodeProps<NodoContenedor>) {
  const { t } = useTranslation();
  return (
    <div
      data-grupo={data.grupo}
      className="h-full w-full rounded-xl border border-dashed border-[var(--border)]/60 bg-[var(--muted)]/40"
    >
      <div
        className="flex items-baseline gap-2 px-5 text-sm font-semibold uppercase tracking-wide text-[var(--muted-foreground)]"
        style={{ height: CABECERA_GRUPO, lineHeight: `${CABECERA_GRUPO}px` }}
      >
        <span>{t(`admin.modelos.${data.grupo}`)}</span>
        <span className="text-xs font-normal normal-case tracking-normal">({data.num})</span>
      </div>
    </div>
  );
}

const TIPOS_NODO = { targetModelo: NodoTargetModelo, grupo: NodoGrupo };

/** Las mismas reglas que `calcularEncuadre`, para el boton de encuadrar de los controles. */
const OPCIONES_ENCUADRE: FitViewOptions = {
  padding: PADDING_ENCUADRE,
  minZoom: ZOOM_ENCUADRE.min,
  maxZoom: ZOOM_ENCUADRE.max,
};

/**
 * Encuadra el grafo: al montar, cada vez que cambia `clave` (entrar o salir de
 * pantalla completa, porque el viewport cambia de tamaño y el encuadre anterior
 * queda mal —a media pantalla o con el grafo en una esquina) y cuando cambian
 * los LIMITES del grafo (el filtro deja otros nodos: lo que queda se encuadra,
 * en vez de quedarse donde estaba el grafo entero).
 *
 * Los limites llegan del LAYOUT, no del store del canvas (ver `limitesLayout`),
 * y se comparan POR VALOR: tras asignar un modelo la lista se recarga y el
 * layout se recalcula igual, y un objeto nuevo con los mismos numeros no puede
 * devolver el viewport al encuadre inicial a espaldas de quien estaba
 * recorriendo el grafo.
 *
 * Sustituye a la prop `fitView` del canvas, y no la acompaña: dos encuadres
 * distintos sobre el mismo evento acabarian pisandose (el de la libreria
 * centra; este alinea arriba cuando hace falta, ver `alinearArriba`) y el
 * ultimo en correr ganaria por orden de efectos, no por diseño.
 *
 * **No usa `fitView()`**: calcula el viewport con `calcularEncuadre` (la misma
 * aritmetica, API publica) y lo aplica con `setViewport`, que es sincrono. El
 * `fitView` de `useReactFlow` es una promesa encolada que la segunda vez —tras
 * pantalla completa— se quedaba sin resolver y dejaba el grafo encuadrado para
 * el alto anterior; el detalle medido esta en `encuadre.ts`.
 *
 * **El cambio de pantalla completa espera a las MEDIDAS NUEVAS del canvas**, no
 * a un numero de frames. Cuando llega `fullscreenchange` el elemento ya tiene
 * el tamaño nuevo, pero React Flow se entera por su `ResizeObserver` y escribe
 * `width`/`height` en su store despues; encuadrar en ese instante usaria las
 * medidas VIEJAS y dejaria el grafo encuadrado para el canvas anterior (medido
 * en Chrome con el arnes: mismo viewport antes y despues, con el canvas ya a
 * otro tamaño). Asi que se deja PENDIENTE y se encuadra cuando `dims` cambia.
 * Un `requestAnimationFrame` doble no vale: en Chrome headless sin frames los
 * rAF no corren nunca, y en un navegador real es apostar a que el observer
 * llega en dos frames. El temporizador de respaldo cubre el caso en que las
 * medidas no cambien (no deberia pasar: pantalla completa siempre cambia el
 * alto) para que un cambio de estado no se quede sin encuadre.
 */
const RESPALDO_ENCUADRE_MS = 300;

function Encuadre({ clave, limites }: { clave: boolean; limites: Rectangulo | null }) {
  const { setViewport } = useReactFlow();
  const store = useStoreApi();
  // Medidas del canvas como texto: cambian de valor solo cuando cambian de
  // verdad, que es lo que este efecto necesita vigilar.
  const dims = useStore((s) => (s.width > 0 && s.height > 0 ? `${s.width}x${s.height}` : ""));

  // `null` = todavia sin encuadre inicial. Guarda `clave` + limites POR VALOR:
  // un layout recalculado con los mismos numeros no vuelve a encuadrar.
  const anterior = useRef<string | null>(null);
  /** Medidas con las que se hizo el ultimo encuadre. */
  const dimsEncuadre = useRef("");
  /** Hay un cambio de pantalla esperando a que el canvas se re-mida. */
  const pendiente = useRef(false);

  useEffect(() => {
    if (dims === "" || !limites) return;

    const encuadrar = () => {
      pendiente.current = false;
      const { width, height } = store.getState();
      dimsEncuadre.current = `${width}x${height}`;
      void setViewport(calcularEncuadre(limites, width, height));
    };

    const estado = `${clave}|${limites.x},${limites.y},${limites.width},${limites.height}`;
    if (anterior.current === estado) {
      // Ni la pantalla ni los limites han cambiado: solo las medidas. Si habia
      // un cambio de pantalla esperando, ESTE es su momento.
      if (pendiente.current) encuadrar();
      return;
    }
    const cambioPantalla = anterior.current !== null && !anterior.current.startsWith(`${clave}|`);
    anterior.current = estado;

    if (!cambioPantalla || dims !== dimsEncuadre.current) {
      // Inicial, filtro, o el canvas ya se re-midio: se encuadra ya.
      encuadrar();
      return;
    }
    // Cambio de pantalla con el canvas aun en las medidas viejas: a esperar.
    pendiente.current = true;
    const respaldo = window.setTimeout(() => {
      if (pendiente.current) encuadrar();
    }, RESPALDO_ENCUADRE_MS);
    return () => window.clearTimeout(respaldo);
  }, [dims, clave, limites, setViewport, store]);
  return null;
}

export default function GrafoModelos({
  asignaciones,
  modelos,
  onAsignar,
  papel,
  resaltados,
  pantallaCompleta = false,
}: {
  /** Ya FILTRADAS: el grafo dibuja lo que recibe. */
  asignaciones: Asignacion[];
  modelos: ModeloCurado[];
  onAsignar: (a: Asignacion, modelId: string | null) => void;
  /**
   * Papel de cada target visible tras el filtro. Sin filtro no se pasa y todos
   * cuentan como `casa`: asi el grafo no tiene que saber si hay filtro o no.
   */
  papel?: Map<string, Coincidencia>;
  /** Subagentes a resaltar porque su padre acaba de cambiar de modelo. */
  resaltados?: Set<string>;
  /**
   * El contenedor del padre esta a pantalla completa: el canvas deja el alto de
   * pagina (`70vh`) y ocupa lo que quede de la ventana. Lo decide el padre
   * porque el elemento que entra en pantalla completa es el suyo (lleva el
   * filtro y el boton de salir, que tienen que seguir dentro).
   */
  pantallaCompleta?: boolean;
}) {
  const { t } = useTranslation();
  const layout = useMemo(() => construirLayout(asignaciones), [asignaciones]);
  const limites = useMemo(() => limitesLayout(layout), [layout]);

  /**
   * Cuantos subagentes arrastraria cada agente.
   *
   * Se cuenta sobre las asignaciones VISIBLES y no sobre la lista completa: si
   * el filtro esconde a un hijo, contarlo aqui prometeria un efecto sobre algo
   * que no esta en pantalla. El aviso describe lo que se ve.
   */
  const arrastres = useMemo(() => contarArrastres(asignaciones), [asignaciones]);

  // El contexto (modelos y el callback) viaja EN el `data` de cada nodo: React
  // Flow no reenvia props a los componentes de nodo, y un contexto de React
  // aparte seria un segundo canal para el mismo dato.
  const nodes = useMemo<Node[]>(
    () =>
      layout.nodes.map((n) =>
        // Los grupos van tal cual: no tienen selector ni nada que asignar.
        n.type === "grupo"
          ? n
          : {
              ...n,
              data: {
                ...n.data,
                modelos,
                onAsignar,
                papel: papel?.get(n.id) ?? "casa",
                arrastra: arrastres.get(n.id) ?? 0,
                arrastrado: resaltados?.has(n.id) ?? false,
              },
            },
      ) as Node[],
    [layout.nodes, modelos, onAsignar, papel, arrastres, resaltados],
  );
  const edges = useMemo<Edge[]>(() => layout.edges as Edge[], [layout.edges]);

  // React Flow es controlado por definicion; como aqui la topologia no se
  // edita, los manejadores de cambio se ignoran a proposito en vez de guardar
  // un estado que nadie modificaria.
  const noop = useCallback(() => {}, []);

  return (
    <div className={pantallaCompleta ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <p className="mb-2 text-sm text-[var(--muted-foreground)]">
        {t("admin.modelos.grafoDescripcion")}
      </p>
      <div
        // Alto generoso y no `h-[400px]`: el grafo real trae 28 cajas, y cuanto
        // menos alto tenga el contenedor mas las encoge `fitView` — con 560 px
        // se encajaban a zoom 0,21 y no se leia ninguna (medido en Chrome).
        // A pantalla completa el `70vh` no vale: el canvas toma lo que la
        // columna flex del padre le deje, que es la ventana menos la barra.
        className={`w-full overflow-hidden rounded-lg border border-[var(--border)] ${
          pantallaCompleta ? "min-h-0 flex-1" : "h-[70vh] min-h-[420px]"
        }`}
        role="application"
        aria-label={t("admin.modelos.grafoAria")}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={TIPOS_NODO}
          onNodesChange={noop}
          onEdgesChange={noop}
          // Solo lectura de TOPOLOGIA: ni se arrastran nodos ni se crean
          // conexiones. El pan y el zoom se quedan: son lo que hace usable el
          // grafo cuando no cabe entero.
          nodesDraggable={false}
          nodesConnectable={false}
          edgesFocusable={false}
          // `elementsSelectable` se queda en TRUE, y no es un descuido. React
          // Flow pone `pointer-events: none` en un nodo que no es arrastrable,
          // ni conectable, NI seleccionable — y entonces el `.react-flow__pane`
          // queda por encima y se come TODOS los eventos: el `<select>` de
          // dentro no abre ni con `nodrag`, que solo evita el arrastre.
          // Medido en Chrome: con `elementsSelectable={false}`,
          // `elementsFromPoint` sobre el desplegable devolvia el pane y el
          // `mousedown` no llegaba al select. Seleccionar un nodo aqui no hace
          // nada (la topologia no se edita), asi que el coste es cero.
          elementsSelectable
          // El encuadre inicial lo hace `<Encuadre>`, no la prop `fitView`.
          minZoom={0.2}
          proOptions={{ hideAttribution: false }}
        >
          <Background />
          <Controls showInteractive={false} fitViewOptions={OPCIONES_ENCUADRE} />
          <Encuadre clave={pantallaCompleta} limites={limites} />
        </ReactFlow>
      </div>
    </div>
  );
}
